import { z } from 'zod';
import type { TranslateFn } from '@/core/i18n/use-intl';
import { jsonSchemaType } from '@/shared/schema';

export const createContractSchema = (
  t: TranslateFn,
  metadataSchema: z.ZodObject<z.ZodRawShape>,
  dynamicMetadataSchema: z.ZodObject<z.ZodRawShape>,
  options?: { requireBilling?: boolean }
) =>
  z
    .object({
      orderNumber: z.string().optional(),
      weaOssCount: z.coerce.number().int().min(0).optional(),
      title: z.string().min(1, t('validation.required')),
      customer: z.string().min(1, t('validation.required')),
      objects: z.array(z.string()).optional(),
      startDate: z.string().min(1, t('validation.required')),
      endDate: z.string().min(1, t('validation.required')),
      note: z.string().optional(),
      status: z.enum(['open', 'closed']).optional(),

      paymentTermsDays: z.coerce.number().int().min(0).optional(),
      billingTotalCents: z.coerce.number().int().min(0).optional(),
      billingInterval: z.enum(['monthly', 'quarterly', 'yearly']).optional(),
      billingGenerationMode: z.enum(['generated', 'manual']).optional(),
      billingPoNumber: z.string().optional(),
      billingContactName: z.string().optional(),
      billingContactEmail: z.union([z.string().email(), z.literal('')]).optional(),
      billingAddress: z.string().optional(),

      billingPeriods: z
        .array(
          z.object({
            id: z.string().optional(),
            startDate: z.string().min(1, t('validation.required')),
            endDate: z.string().min(1, t('validation.required')),
            amountCents: z.coerce.number().int().min(0),
            status: z.enum(['scheduled', 'billable', 'invoiced', 'paid']),
            dueDate: z.string().optional(),
            invoiceNumber: z.string().optional(),
            note: z.string().optional(),
          })
        )
        .optional(),

      metadata: metadataSchema,
      dynamicMetadataSchema: jsonSchemaType.optional(),
      dynamicMetadataValues: dynamicMetadataSchema,
    })
    .superRefine((data, ctx) => {
      const startTimestamp = Date.parse(data.startDate);
      const endTimestamp = Date.parse(data.endDate);
      if (
        Number.isFinite(startTimestamp) &&
        Number.isFinite(endTimestamp) &&
        endTimestamp < startTimestamp
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['startDate'],
          message: t('validation.StartDateAfterEndDate'),
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endDate'],
          message: t('validation.EndDateBeforeStartDate'),
        });
      }

      if (options?.requireBilling) {
        if (typeof data.billingTotalCents !== 'number' || data.billingTotalCents <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['billingTotalCents'],
            message: t('validation.required'),
          });
        }
        if (!data.billingInterval) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['billingInterval'],
            message: t('validation.required'),
          });
        }
      }
    });
